import { PaginatedResponse } from "interfaces/paginatedResponse";
import api from "./api";
import type {
  IProdutoLoja,
  IEspecificacaoProduto,
  ITabelaTamanho,
  IVariacaoProdutoPayload,
  StatusProduto,
  IProdutoLojaCreate,
} from "interfaces/loja";
import { ENDPOINTS } from "./endPoints";

// API: Listar produtos da loja
export async function listarProdutosLoja(params?: {
  query?: string;
  status?: StatusProduto;
  page?: number;
  limit?: number;
}): Promise<{ result: PaginatedResponse<IProdutoLoja>; status: number }> {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.GERENCIAR_LISTAR_ALL;
  try {
    // Construir a query string
    const query = new URLSearchParams();
    // Adicionar os parâmetros da query
    if (params?.query) query.set("query", String(params.query));
    if (params?.status) query.set("status", String(params.status));
    if (params?.page != null) query.set("page", String(params.page));
    if (params?.limit != null) query.set("limit", String(params.limit));

    // // Join the query string
    const joinQuery = query.toString() ? `?${query}` : "";

    const { data, status } = await api({
      url: url(joinQuery),
      method: method,
    });
    return { result: data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Detalhar produto da loja
export async function detalharProdutoByIdLoja(
  produtoId: string
): Promise<{ data: IProdutoLoja; status: number }> {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.GERENCIAR_DETALHE_POR_ID;
  try {
    const { data, status } = await api({
      url: url(produtoId),
      method: method,
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Listar produtos da loja consulta em LOTES por ids
export async function listarProdutosLojaLotePorIds(produtosIds: string[]) {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.LISTAR_ALL_LOTES_IDS;
  try {
    const { data, status } = await api({
      url: url,
      method: method,
      data: { produtos: produtosIds },
    });
    const produtos = Array.isArray(data)
      ? data
      : ((data as { data?: IProdutoLoja[] })?.data ?? []);
    return { data: produtos, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Editar produto da loja
export async function createOrEditProdutoLoja(produto: IProdutoLojaCreate) {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.CRIAR_ATUALIZAR;
  try {
    const { data, status } = await api({
      url: url,
      method: method,
      data: produto,
    });
    return { data, status };
  } catch (error: any) {
    const msg =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "Falha na comunicação";
    throw new Error(msg);
  }
}

// Envia imagens do produto via multipart/form-data (arquivos reais)
export async function enviarImagensProdutoLoja({
  produtoId,
  variacaoProdutoId,
  arquivos,
}: {
  produtoId: string;
  variacaoProdutoId?: string;
  arquivos: File[];
}) {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.ENVIAR_IMAGES;
  const urlWithParams = url(produtoId, variacaoProdutoId);
  const formData = new FormData();

  arquivos.forEach((file) => {
    formData.append("files", file, file.name);
  });

  const response = await api({
    url: urlWithParams,
    method: method,
    data: formData,
  });

  if (!response || response.status >= 400) {
    throw new Error(
      response?.data?.error ||
        response?.data?.message ||
        `Falha ao salvar imagens (status ${response?.status ?? "sem resposta"})`
    );
  }

  return { data: response.data, status: response.status };
}

// API: Excluir imagem do produto
export async function removerImagemProdutoLoja(filename: string) {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.REMOVER_IMAGES;
  try {
    const { data, status } = await api({
      url: url(filename),
      method: method,
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha ao excluir imagem");
  }
}

// Salva especificações do produto
export async function salvarEspecificacoesProdutoLoja({
  produtoId,
  especificacoes,
}: {
  produtoId: string;
  especificacoes: IEspecificacaoProduto[];
}) {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.DEFINIR_ESPECIFICACOES;

  try {
    const { data, status } = await api({
      url: url(produtoId),
      method: method,
      data: { especificacoes },
    });

    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha ao salvar especificações");
  }
}

// Remover especificação do produto
export async function removerEspecificacaoProdutoLoja(especificacaoId: number) {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.REMOVER_ESPECIFICACOES;
  try {
    const { data, status } = await api({
      url: url(especificacaoId),
      method: method,
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha ao remover especificação");
  }
}

// Salva tabelas de tamanho do produto (array em uma chamada)
export async function salvarTabelaTamanhoProdutoLoja(
  produtoId: string,
  tabelas: ITabelaTamanho[]
) {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.DEFINIR_TABELA_TAMANHO;
  try {
    const { data, status } = await api({
      url: url(produtoId),
      method: method,
      data: { tabelas },
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha ao salvar tabelas de tamanho");
  }
}

export async function removerTabelaTamanhoProdutoLoja(tabelaId: number) {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.REMOVER_TABELA_TAMANHO;
  try {
    const { data, status } = await api({
      url: url(tabelaId),
      method: method,
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha ao remover tabela de tamanho");
  }
}

// Salva variações do produto (payload: sku, preco, precoOriginal, estoque, emEstoque, atributos)
export async function salvarVariacoesProdutoLoja(
  produtoId: string,
  variacoes: IVariacaoProdutoPayload[]
): Promise<{ data: IProdutoLoja; status: number }> {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.DEFINIR_VARIACOES;
  const urlWithParams = url(produtoId);
  try {
    const { data, status } = await api({
      url: urlWithParams,
      method: method,
      data: { variacoes },
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha ao salvar variações");
  }
}

// Salva vínculos de categorias do produto
export async function salvarCategoriasProdutoLoja(
  produtoId: string,
  categorias: number[]
) {
  try {
    const { data, status } = await api.put(
      `/loja/produtos/${produtoId}/categorias`,
      {
        categorias,
      }
    );
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha ao salvar categorias do produto");
  }
}

// API: Deletar produto da loja
export async function deletarProdutoLoja(produtoId: string) {
  try {
    const { data, status } = await api.delete(`/loja/produtos/${produtoId}`);
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// Público: listar produtos
export async function listarProdutosPublicos(params?: {
  query?: string;
  categoria?: string | number;
  eventoId?: number;
  page?: number;
  limit?: number;
}): Promise<{ data: PaginatedResponse<IProdutoLoja>; status: number }> {
  try {
    const query = new URLSearchParams();

    if (params?.query) query.set("query", String(params.query));
    if (params?.categoria) query.set("categoria", String(params.categoria));
    if (params?.eventoId != null)
      query.set("eventoId", String(params.eventoId));
    if (params?.page != null) query.set("page", String(params.page));
    if (params?.limit != null) query.set("limit", String(params.limit));

    const joinQuery = query.toString() ? `?${query}` : "";
    const url = `/loja/produtos${joinQuery}`;

    const { data, status } = await api.get(url);
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// Público: buscar produto por slug
export async function buscarProdutoPublicoPorSlug(slug: string) {
  try {
    const { data, status } = await api.get(`/loja/produtos/${slug}`);
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// Público: produtos por evento
export async function listarProdutosPorEvento(
  eventoId: number,
  pagina?: number,
  limite?: number
) {
  try {
    const query = new URLSearchParams();
    if (pagina != null) query.set("pagina", String(pagina));
    if (limite != null) query.set("limite", String(limite));
    const { data, status } = await api.get(
      `/evento/${eventoId}/produtos${query.toString() ? `?${query}` : ""}`
    );
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// Público: vincular categorias ao produto
export async function vincularCategoriasProdutoLoja(
  produtoId: string,
  categorias: number[]
) {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.VINCULAR_CATEGORIA_PRODUTO;
  try {
    const urlWithParams = url(produtoId);
    const { data, status } = await api({
      url: urlWithParams,
      method: method,
      data: { categorias },
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// Público: desvincular categorias do produto
export async function desvincularCategoriasProdutoLoja(
  produtoId: string,
  categorias: number[]
) {
  const { url, method } = ENDPOINTS.LOJA.PRODUTOS.DESVINCULAR_CATEGORIA_PRODUTO;
  try {
    const urlWithParams = url(produtoId);
    const { data, status } = await api({
      url: urlWithParams,
      method: method,
      data: { categorias },
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// Público: listar cupons vinculados ao produto
export async function listarCuponsVinculadosProdutoLoja(produtoId: string) {
  const { url, method } = ENDPOINTS.LOJA.CUPOM_LOJA.LISTAR_CUPOM_PRODUTOS;
  try {
    const urlWithParams = url(produtoId);
    const { data, status } = await api({
      url: urlWithParams,
      method: method,
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}
